import { useCallback, useMemo, useState } from 'react'
import type { MappingRule, ParsedFile } from '../types'
import { exportTransformedCsv } from '../utils/exportCsv'
import { transformRows } from '../utils/transformer'

const PREVIEW_ROW_LIMIT = 50

export default function usePreviewData(
  parsedFile: ParsedFile | null,
  mappingRules: MappingRule[],
  computedFields: MappingRule[] = []
) {
  const [previewEnabled, setPreviewEnabled] = useState(true)

  const previewSourceData = useMemo(() => {
    if (!parsedFile) return []
    return parsedFile.rows.slice(0, PREVIEW_ROW_LIMIT).map((row, i) => ({
      key: String(i),
      _rowNum: i + 1,
      ...row,
    }))
  }, [parsedFile])

  const previewTransformedData = useMemo(() => {
    if (!parsedFile) return []
    const transformed = transformRows(
      mappingRules,
      parsedFile.rows.slice(0, PREVIEW_ROW_LIMIT),
      computedFields
    )
    return transformed.map((row, i) => ({
      key: String(i),
      _rowNum: i + 1,
      ...row,
    }))
  }, [parsedFile, mappingRules, computedFields])

  const sourceTableColumns = useMemo(() => {
    if (!parsedFile) return []
    return [
      { title: '#', dataIndex: '_rowNum', key: '_rowNum', width: 40 },
      ...parsedFile.columns.map((col) => ({
        title: col,
        dataIndex: col,
        key: col,
        ellipsis: true,
      })),
    ]
  }, [parsedFile])

  const transformedTableColumns = useMemo(() => {
    const targets = mappingRules.map((r) => r.target).filter(Boolean)
    if (targets.length === 0) return []
    // Surface the `Errors` column (populated by concat rules that skip a
    // record) only when at least one previewed row actually reported an error.
    const hasErrors = previewTransformedData.some(
      (row) => (row as { Errors?: string }).Errors
    )
    return [
      { title: '#', dataIndex: '_rowNum', key: '_rowNum', width: 40 },
      ...targets.map((t) => ({
        title: t,
        dataIndex: t,
        key: t,
        ellipsis: true,
      })),
      ...(hasErrors
        ? [
            {
              title: 'Errors',
              dataIndex: 'Errors',
              key: 'Errors',
              ellipsis: true,
            },
          ]
        : []),
    ]
  }, [mappingRules, previewTransformedData])

  const handleExportResult = useCallback(() => {
    if (!parsedFile) return
    exportTransformedCsv(mappingRules, parsedFile.rows, computedFields)
  }, [parsedFile, mappingRules, computedFields])

  const showPreview =
    previewEnabled && parsedFile !== null && mappingRules.length > 0

  return {
    previewEnabled,
    setPreviewEnabled,
    previewSourceData,
    previewTransformedData,
    sourceTableColumns,
    transformedTableColumns,
    handleExportResult,
    showPreview,
    previewRowLimit: PREVIEW_ROW_LIMIT,
  }
}
